/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/AssistantSelectionToolbar.tsx
 * PURPOSE: Perplexity-style floating “Ask AI” when the user selects text in main content; opens Langflow
 *          assistant with a quoted prompt.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { dispatchAssistantOpenWithPrefill } from './assistantPrefillBridge';

export interface AssistantSelectionToolbarProps {
  /** Root element for allowed selections (default `#main-content`). */
  rootSelector?: string;
  /** When false, listeners are not attached. */
  enabled?: boolean;
}

const MIN_CHARS = 10;
const MAX_QUOTE_CHARS = 6000;

function getRoot(rootSelector: string): Element | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector(rootSelector);
}

function selectionInsideRoot(root: Element, sel: Selection): boolean {
  if (!sel.rangeCount) return false;
  const node = sel.getRangeAt(0).commonAncestorContainer;
  const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element | null);
  if (!el) return false;
  return root.contains(el);
}

function isInsideLangflowPanel(el: Element | null): boolean {
  return Boolean(el?.closest('#brazn-ai-assistant-panel'));
}

function isInsideFormField(el: Element | null): boolean {
  return Boolean(el?.closest('input, textarea, select, [contenteditable="true"]'));
}

function buildQuotedPrompt(selected: string, pageTitle: string): string {
  const body = selected.trim().slice(0, MAX_QUOTE_CHARS);
  const truncated = selected.trim().length > MAX_QUOTE_CHARS ? '\n\n_(Selection truncated.)_' : '';
  const title = pageTitle.replace(/\s+/g, ' ').trim().slice(0, 100);
  const titleLine = title ? ` from “${title}”` : '';
  return `[Quoted selection${titleLine}]\n\n"""\n${body}\n"""${truncated}\n\nWhat should I do with this?`;
}

export function AssistantSelectionToolbar({
  rootSelector = '#main-content',
  enabled = true,
}: AssistantSelectionToolbarProps) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const savedTextRef = useRef('');

  const hide = useCallback(() => {
    setVisible(false);
    savedTextRef.current = '';
  }, []);

  const updateFromSelection = useCallback(() => {
    if (!enabled) return;
    const root = getRoot(rootSelector);
    if (!root) {
      hide();
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      hide();
      return;
    }
    const text = sel.toString().trim();
    if (text.length < MIN_CHARS) {
      hide();
      return;
    }
    if (!selectionInsideRoot(root, sel)) {
      hide();
      return;
    }
    const focusNode = sel.focusNode;
    const focusEl =
      focusNode?.nodeType === Node.TEXT_NODE ? focusNode.parentElement : (focusNode as Element | null);
    if (!focusEl || isInsideLangflowPanel(focusEl) || isInsideFormField(focusEl)) {
      hide();
      return;
    }

    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      hide();
      return;
    }

    savedTextRef.current = text;
    const margin = 8;
    const width = 200;
    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
    const top = rect.bottom + margin;
    setPos({ top, left });
    setVisible(true);
  }, [enabled, hide, rootSelector]);

  useEffect(() => {
    if (!enabled) return;
    let debounceTimer = 0;
    const schedule = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        updateFromSelection();
      }, 40);
    };

    document.addEventListener('mouseup', schedule);
    document.addEventListener('selectionchange', schedule);
    const onScroll = () => hide();
    window.addEventListener('scroll', onScroll, true);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(debounceTimer);
      document.removeEventListener('mouseup', schedule);
      document.removeEventListener('selectionchange', schedule);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [enabled, hide, updateFromSelection]);

  const onAskPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const onAskClick = () => {
    const raw = savedTextRef.current;
    hide();
    window.getSelection()?.removeAllRanges();
    if (!raw.trim()) return;
    const pageTitle = typeof document !== 'undefined' ? document.title : '';
    dispatchAssistantOpenWithPrefill(buildQuotedPrompt(raw, pageTitle));
  };

  if (!visible) return null;

  return (
    <div
      className="pointer-events-auto fixed z-[60] flex items-center rounded-lg border border-gray-200 bg-white py-1 pl-1 pr-1.5 shadow-xl dark:border-gray-600 dark:bg-gray-800"
      style={{ top: pos.top, left: pos.left }}
      role="presentation"
    >
      <button
        type="button"
        onPointerDown={onAskPointerDown}
        onClick={onAskClick}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-500"
        aria-label="Ask AI about this selection"
        data-testid="assistant-selection-ask-ai"
      >
        <SparklesIcon className="h-4 w-4 shrink-0" aria-hidden />
        Ask AI
      </button>
    </div>
  );
}
